import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { EMPTY, map, Observable, Subject, withLatestFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaCardComponent } from '@priva/components/card';

import { selectApiVersion } from 'app/state/api-config/api-config.selectors';
import { updateRecentRowNumber } from 'app/state/green-house-map/green-house-map.actions';
import { selectPreliminaryRowRegistrationStatus } from 'app/state/registrations/registrations.selectors';
import {
    startRowRegistrationStatusSelection,
    toggleRowSelectionAndFinishIfSingleSelection,
} from 'app/state/row-selection/row-selection.actions';
import { getRowSelectionByRowNumber } from 'app/state/row-selection/row-selection.selectors';

import { ApiVersion } from '../../../../models';
import { getIconClassForRegistrationStatus, Row, RowRegistrationStatus } from '../../models';

@Component({
    selector: 'app-map-row',
    templateUrl: './map-row.component.html',
    styleUrl: './map-row.component.scss',
    imports: [AsyncPipe, FormsModule, PrivaButtonComponent, PrivaCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapRowComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private store = inject(Store);

    activityNumber = input(0);
    row = input<Row | undefined>(undefined);

    registrationStatus$: Observable<RowRegistrationStatus | 'not-registered'> = EMPTY;
    isRowSelected$: Observable<boolean> = EMPTY;

    private contextMenuTrigger$ = new Subject<Event | undefined>();

    isMultiSelectModeAllowed$ = this.store.pipe(
        select(selectApiVersion),
        map((apiVersion) => apiVersion >= ApiVersion.MultiRegistrationLocations),
    );

    getIconClassForRegistrationStatus(status: RowRegistrationStatus | 'not-registered'): string {
        if (status === 'not-registered') return '';
        return getIconClassForRegistrationStatus(status);
    }

    constructor() {
        this.contextMenuTrigger$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                withLatestFrom(this.isMultiSelectModeAllowed$),
                filter(([_, isMultiSelectModeAllowed]) => isMultiSelectModeAllowed),
            )
            .subscribe(([event]) => {
                event?.preventDefault();
                this.openRowStatusSelectDialog();
            });
    }

    ngOnInit(): void {
        if (!this.row()) return;

        const rowNumber = this.row().number;

        const currentRowSelection$ = this.store.select(getRowSelectionByRowNumber(rowNumber));

        this.isRowSelected$ = currentRowSelection$.pipe(map((currentRowSelection) => !!currentRowSelection));

        this.registrationStatus$ = this.store.pipe(
            select(
                selectPreliminaryRowRegistrationStatus({
                    activityNumber: this.activityNumber(),
                    rowNumber: this.row().number,
                }),
            ),
            map((status) => status || 'not-registered'),
        );
    }

    toggleRowSelectionAndFinishIfSingleSelection(): void {
        if (!this.row()) return;

        this.store.dispatch(updateRecentRowNumber({ recentRowNumber: this.row().number }));
        this.store.dispatch(toggleRowSelectionAndFinishIfSingleSelection({ rowNumber: this.row().number }));
    }

    onContextMenu(event: Event) {
        this.contextMenuTrigger$.next(event);
    }

    onMenuButtonClicked(event: Event) {
        event?.stopPropagation();
        this.openRowStatusSelectDialog();
    }

    private openRowStatusSelectDialog() {
        if (!this.row()) return;

        this.store.dispatch(
            startRowRegistrationStatusSelection({
                activityNumber: this.activityNumber(),
                rowNumber: this.row().number,
            }),
        );
    }
}
